import React, { useState } from "react";
import Input from "../Inputs/Input/Input";
import InputCheckbox from "../Inputs/InputCheckbox/InputCheckbox";
import InputDropdown from "../../inputs/InputDropdown/InputDropdown";
import InputTextarea from "../Inputs/InputTextarea/InputTextarea";
import InputDocument from "../Inputs/InputDocument/InputDocument";
import InputRadioGroup from "../Inputs/InputRadioGroup/InputRadioGroup";
import type { FormElement, FormGroup, Option } from "../../../types/types";
import "./FormModal.scss";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import InputDropdownFilter from "../Inputs/InputDropdownFilter/InputDropdownFilter";
import InputDate from "../Inputs/InputDate/InputDate";
import InputDropdown_New from "../../inputs/InputDropdown/InputDropdown_New";

interface FormComponentProps {
    dynamicFormElements: FormGroup[];
    modalData: { [key: string]: any };
    associatedOptions: { [key: string]: any[] };
    handleInputChange: (name: string, value: any) => void;
    missingNames?: string[];
}

const FormComponent: React.FC<FormComponentProps> = ({
    dynamicFormElements,
    modalData,
    associatedOptions,
    handleInputChange,
    missingNames = [],
}) => {
    const [currentGroupIndex, setCurrentGroupIndex] = useState<number>(0);

    const visibleGroups = dynamicFormElements.filter((group: FormGroup) =>
        group.elements.some((element: FormElement) => element.visible)
    );

    const totalGroups = visibleGroups.length;
    const currentGroup = visibleGroups[currentGroupIndex];

    const handleNext = (): void => {
        setCurrentGroupIndex((prev: number) =>
            Math.min(prev + 1, totalGroups - 1)
        );
    };

    const handlePrevious = (): void => {
        setCurrentGroupIndex((prev: number) => Math.max(prev - 1, 0));
    };

    if (!currentGroup) return null;

    return (
        <form className="modal-form">
            <div className="form-group">
                {currentGroup.titleVisible && (
                    <div className="form-group-title">
                        <button
                            type="button"
                            onClick={handlePrevious}
                            className="navigation-button left"
                            disabled={currentGroupIndex === 0}
                        >
                            {IoIosArrowBack({ size: 16 })}
                        </button>
                        <h3>{currentGroup.title}</h3>
                        <button
                            type="button"
                            onClick={handleNext}
                            className="navigation-button right"
                            disabled={currentGroupIndex === totalGroups - 1}
                        >
                            {IoIosArrowForward({ size: 16 })}
                        </button>
                    </div>
                )}
                <div className="form-group-elements modal-form-wrapper">
                    {currentGroup.elements
                        .filter((element: FormElement) => element.visible)
                        .map((element: FormElement) => {
                            let value = element.defaultValue ?? "";
                            value = modalData[element.name] ?? "";

                            let selectedOption = null;
                            let options: {
                                value: string;
                                label: string;
                                id?: number | undefined;
                                fatherId?: number | undefined;
                                associatedField?: string;
                                valueField?: string;
                            }[] = [];

                            if (element.type === "select") {
                                value =
                                    element.options?.find(
                                        (x) =>
                                            x.id ===
                                                (modalData[
                                                    x.associatedField ?? ""
                                                ] === null ||
                                                modalData[
                                                    x.associatedField ?? ""
                                                ]?.id === undefined
                                                    ? modalData[
                                                          x.associatedField ??
                                                              ""
                                                      ]
                                                    : modalData[
                                                          x.associatedField ??
                                                              ""
                                                      ]?.id) || ""
                                    )?.value || "";

                                options = element.associatedFormElement
                                    ? associatedOptions[element.name] || []
                                    : element.options || [];

                                const rawValue = modalData[element.name];

                                const findOption = (
                                    val: any
                                ): Option | undefined => {
                                    if (val == null) return undefined;
                                    // Si es objeto con id o value
                                    if (
                                        typeof val === "object" &&
                                        (val.id || val.value)
                                    ) {
                                        const id = val.id ?? val.value;
                                        return options.find(
                                            (o) =>
                                                String(o.value) === String(id)
                                        );
                                    }
                                    // Primitivo: intentar value o label
                                    return (
                                        options.find(
                                            (o) =>
                                                String(o.value) === String(val)
                                        ) ||
                                        options.find(
                                            (o) =>
                                                String(o.label) === String(val)
                                        ) ||
                                        options.find(
                                            (o) => String(o.id) === String(val)
                                        )
                                    );
                                };
                                selectedOption = findOption(rawValue) ?? null;
                            }

                            const dynamicOptions =
                                element.type === "select" &&
                                associatedOptions[element.name]
                                    ? associatedOptions[element.name]
                                    : [];

                            const resolvedOptions =
                                Array.isArray(options) && options.length > 0
                                    ? options
                                    : Array.isArray(element.options) &&
                                        element.options.length > 0
                                      ? element.options
                                      : [];

                            switch (element.type) {
                                case "text":
                                case "email":
                                case "password":
                                case "number":
                                case "tel":
                                    return (
                                        <Input
                                            key={element.name}
                                            type={element.type}
                                            label={element.label}
                                            placeholder={element.placeholder}
                                            value={value}
                                            onChange={(
                                                value: React.ChangeEvent<HTMLInputElement>
                                            ) =>
                                                handleInputChange(
                                                    element.name,
                                                    value.target.value
                                                )
                                            }
                                            required={element.required}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                case "checkbox":
                                    return (
                                        <InputCheckbox
                                            key={element.name}
                                            id={element.name}
                                            label={element.label}
                                            checked={Boolean(value)}
                                            onChange={(
                                                e: React.ChangeEvent<HTMLInputElement>
                                            ) =>
                                                handleInputChange(
                                                    element.name,
                                                    e.target.checked
                                                )
                                            }
                                            required={element.required}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                case "select":
                                    console.log(
                                        "rawValue:",
                                        element,
                                        "options:",
                                        options,
                                        "selectedOption:",
                                        selectedOption
                                    );
                                    return (
                                        <InputDropdown_New
                                            key={`${element.name}-${options.length}`}
                                            label={element.label}
                                            options={resolvedOptions}
                                            placeholder={element.placeholder}
                                            defaultValue={selectedOption}
                                            onChange={(
                                                opt:
                                                    | Option
                                                    | Option[]
                                                    | string
                                                    | number
                                                    | (string | number)[]
                                                    | null
                                            ) => {
                                                const newValue = Array.isArray(
                                                    opt
                                                )
                                                    ? opt.map((option) =>
                                                          typeof option ===
                                                              "object" &&
                                                          option !== null &&
                                                          "value" in option
                                                              ? (
                                                                    option as Option
                                                                ).value
                                                              : option
                                                      )
                                                    : (opt as Option)?.value;

                                                handleInputChange(
                                                    element.name,
                                                    newValue
                                                );
                                            }}
                                            multiple={
                                                element.withCheckbox || false
                                            }
                                            required={element.required}
                                            disabled={element.disabled}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );

                                case "multiselect":
                                    return (
                                        <InputDropdown_New
                                            label={element.label}
                                            options={element.options || []}
                                            // defaultValue={
                                            //     Array.isArray(value)
                                            //         ? value
                                            //         : value
                                            //           ? [value]
                                            //           : []
                                            // }

                                            defaultValue={
                                                Array.isArray(value)
                                                    ? value
                                                    : value
                                                      ? [value]
                                                      : []
                                            } // ✅ CONTROLADO
                                            placeholder={element.placeholder}
                                            searchable={true}
                                            multiple={true}
                                            returnFormat="value"
                                            // onChange={(
                                            //     selected:
                                            //         | Option
                                            //         | Option[]
                                            //         | string
                                            //         | number
                                            //         | (string | number)[]
                                            //         | null
                                            // ) => {
                                            //     handleInputChange(
                                            //         element.name,
                                            //         selected || []
                                            //     );
                                            // }}

                                            onChange={(selected) => {
                                                console.log(
                                                    "selected:",
                                                    selected
                                                );
                                                console.log(
                                                    "isArray:",
                                                    Array.isArray(selected)
                                                );
                                                console.log(
                                                    "type:",
                                                    typeof selected
                                                );
                                                if (selected == null) return;
                                                handleInputChange(
                                                    element.name,
                                                    selected ?? []
                                                );
                                            }}
                                            required={element.required}
                                            disabled={element.disabled}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                case "textarea":
                                    return (
                                        <InputTextarea
                                            key={element.name}
                                            label={element.label}
                                            placeholder={element.placeholder}
                                            value={value}
                                            onChange={(
                                                e: React.ChangeEvent<HTMLTextAreaElement>
                                            ) =>
                                                handleInputChange(
                                                    element.name,
                                                    e.currentTarget.value
                                                )
                                            }
                                            required={element.required}
                                            disabled={element.disabled}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                case "document":
                                    return (
                                        <InputDocument
                                            key={element.name}
                                            onFileChange={(value: any) =>
                                                handleInputChange(
                                                    element.name,
                                                    value
                                                )
                                            }
                                            label={element.label}
                                            required={element.required}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                case "radio":
                                    return (
                                        <InputRadioGroup
                                            key={element.name}
                                            name={element.name}
                                            options={element.options!.map(
                                                (group: any) => ({
                                                    label: group.label,
                                                    options: Array.isArray(
                                                        group.options
                                                    )
                                                        ? group.options.map(
                                                              (opt: any) => ({
                                                                  value: String(
                                                                      opt.value
                                                                  ),
                                                                  label: opt.label,
                                                              })
                                                          )
                                                        : [],
                                                })
                                            )}
                                            selectedValue={value}
                                            onChange={(value: any) =>
                                                handleInputChange(
                                                    element.name,
                                                    value
                                                )
                                            }
                                            required={element.required}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                case "date":
                                    return (
                                        <InputDate
                                            key={element.name}
                                            label={element.label}
                                            name={element.name}
                                            value={value}
                                            onChange={(date: any) =>
                                                handleInputChange(
                                                    element.name,
                                                    date.target.value
                                                )
                                            }
                                            required={element.required}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                default:
                                    return (
                                        <div key={element.name}>
                                            <strong>
                                                Unsupported element:{" "}
                                            </strong>
                                            {element.type}
                                        </div>
                                    );
                            }
                        })}
                </div>
            </div>
        </form>
    );
};

export default FormComponent;
